/**
 * PreferenciasQuietHours — Horario de silencio + timezone.
 *
 * Spec docs/notificacoes/IMPLEMENTATION_SPEC_V1V2.md §3.4 #2 / §3.8 item 2.
 *
 * Wired to backend (TASK-15 do plano notifications gaps fix, 2026-05-19):
 *   - GET    /api/v1/me/quiet-hours pra hidratar form inicial
 *   - PATCH  /api/v1/me/quiet-hours pra persistir
 *
 * Eventos criticos podem pular quiet hours via except_severity (default null
 * = todas severidades respeitam a janela).
 */
import { Stack, Group, ThemeIcon, Text, Select, Button, Skeleton } from '@mantine/core'
import { TimeInput } from '@mantine/dates'
import { notifications } from '@mantine/notifications'
import { IconMoon } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  invalidateMeNotificationQueries,
  notificationPreferencesApi,
  type QuietHoursPayload,
} from '@/api/notification-preferences'
import { formatApiError } from '@/api/errors'
import { WidgetCard } from '@/components/ui'

const TIMEZONE_OPTIONS = [
  { value: 'America/Manaus', label: 'America/Manaus (AMT, UTC-4)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT, UTC-3)' },
  { value: 'America/Belem', label: 'America/Belem (UTC-3)' },
  { value: 'America/Cuiaba', label: 'America/Cuiaba (AMT, UTC-4)' },
  { value: 'America/Porto_Velho', label: 'America/Porto_Velho (UTC-4)' },
  { value: 'America/Rio_Branco', label: 'America/Rio_Branco (UTC-5)' },
  { value: 'UTC', label: 'UTC (UTC+0)' },
]

const DEFAULT_START = '22:00'
const DEFAULT_END = '07:00'
const DEFAULT_TZ = 'America/Manaus'

const QUERY_KEY = ['me', 'quiet-hours'] as const

export function PreferenciasQuietHours() {
  const qc = useQueryClient()
  const [start, setStart] = useState(DEFAULT_START)
  const [end, setEnd] = useState(DEFAULT_END)
  const [tz, setTz] = useState<string>(DEFAULT_TZ)

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => notificationPreferencesApi.getQuietHours(),
  })

  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    if (!data || hydrated) return
    if (data.start_hh_mm) setStart(data.start_hh_mm)
    if (data.end_hh_mm) setEnd(data.end_hh_mm)
    if (data.timezone) setTz(data.timezone)
    setHydrated(true)
  }, [data, hydrated])

  const mutation = useMutation({
    mutationFn: (payload: QuietHoursPayload) =>
      notificationPreferencesApi.updateQuietHours(payload),
    onMutate: async (newPayload) => {
      await qc.cancelQueries({ queryKey: QUERY_KEY })
      const prev = qc.getQueryData<QuietHoursPayload>(QUERY_KEY)
      qc.setQueryData<QuietHoursPayload>(QUERY_KEY, {
        start_hh_mm: newPayload.start_hh_mm,
        end_hh_mm: newPayload.end_hh_mm,
        timezone: newPayload.timezone,
        except_severity: newPayload.except_severity ?? null,
      })
      return { prev }
    },
    onSuccess: () => {
      invalidateMeNotificationQueries(qc)
      notifications.show({
        color: 'green',
        title: 'Salvo',
        message: 'Quiet hours atualizadas. O digest respeitara a janela.',
      })
    },
    onError: (err: unknown, _vars, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(QUERY_KEY, ctx.prev)
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: formatApiError(err, 'Falha ao salvar quiet hours.'),
      })
    },
  })

  const handleSave = () => {
    if (!start || !end) {
      notifications.show({
        color: 'red',
        title: 'Campos obrigatórios',
        message: 'Defina horário de início e fim do silêncio.',
      })
      return
    }
    mutation.mutate({
      start_hh_mm: start,
      end_hh_mm: end,
      timezone: tz,
      except_severity: null,
    })
  }

  if (isLoading) {
    return (
      <WidgetCard p="md">
        <Stack gap="sm">
          <Skeleton height={32} />
          <Skeleton height={60} />
          <Skeleton height={36} />
        </Stack>
      </WidgetCard>
    )
  }

  const isDirty =
    start !== (data?.start_hh_mm ?? DEFAULT_START) ||
    end !== (data?.end_hh_mm ?? DEFAULT_END) ||
    tz !== (data?.timezone ?? DEFAULT_TZ)

  return (
    <WidgetCard p="md">
      <Stack gap="sm">
        <Group gap={8} align="center">
          <ThemeIcon size={32} radius="md" color="indigo" variant="light">
            <IconMoon size={18} />
          </ThemeIcon>
          <Text fw={600} size="sm">Horário de silêncio</Text>
        </Group>

        <Text size="xs" c="dimmed">
          Durante esse intervalo, notificações não-críticas são retidas
          (entrega no próximo período ativo). Eventos criticos/fatais
          ignoram quiet hours.
        </Text>

        {(data?.start_hh_mm || data?.end_hh_mm) && (
          <Group gap={6} c="dimmed">
            <Text size="xs">Estado atual salvo:</Text>
            <Text size="xs" fw={600}>
              {data.start_hh_mm ?? '—'} → {data.end_hh_mm ?? '—'}
              {data.timezone ? ` (${data.timezone})` : ''}
            </Text>
          </Group>
        )}

        <Group gap="sm" grow>
          <TimeInput
            label="Início"
            value={start}
            onChange={(e) => setStart(e.currentTarget.value)}
          />
          <TimeInput
            label="Fim"
            value={end}
            onChange={(e) => setEnd(e.currentTarget.value)}
          />
        </Group>

        <Select
          label="Fuso horário"
          data={TIMEZONE_OPTIONS}
          value={tz}
          onChange={(v) => setTz(v ?? DEFAULT_TZ)}
          searchable
          allowDeselect={false}
        />

        <Group justify="flex-end">
          <Button
            size="sm"
            color="indigo"
            loading={mutation.isPending}
            onClick={handleSave}
            disabled={!isDirty}
          >
            {isDirty ? 'Salvar' : 'Sem mudanças'}
          </Button>
        </Group>
      </Stack>
    </WidgetCard>
  )
}
